import { mockStore } from "@/data/mock/mock-store";
import { normalizeArrayResponse } from "@/data/source/response-normalizer";
import { URLList } from "@/data/url_list";
import { CreateDoctorPayload } from "@/domain/repository/doctor-repository";
import { createLogger, withApiLogging } from "@/utils/logger/logger";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATResult } from "@sadtx/sat-error";

export class DoctorSource {
  private readonly logger = createLogger("DoctorSource");

  constructor(private readonly client: FetchApiClientMethod) {}

  async fetchDoctors(): Promise<SATResult<unknown[], SATError>> {
    const url = URLList.DOCTOR.LIST;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(normalizeArrayResponse(data, DOCTOR_LIST_RESPONSE_KEYS));
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async fetchChargeDoctors(): Promise<SATResult<unknown[], SATError>> {
    const url = URLList.DOCTOR.CHARGE_LIST;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(normalizeArrayResponse(data, DOCTOR_LIST_RESPONSE_KEYS));
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async fetchDoctor(uuid: string): Promise<SATResult<unknown, SATError>> {
    const url = `${URLList.DOCTOR.DETAIL}/${uuid}`;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(data);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async createMedicalExpert(payload: CreateDoctorPayload): Promise<SATResult<void, SATError>> {
    if (import.meta.env.MODE === "mock") {
      mockStore.addDoctor(payload);
      return SATResult.success(void 0);
    }

    return this.createMedicalExpertByUrl(URLList.DOCTOR.CREATE_DOCTOR, payload);
  }

  async createNurse(payload: CreateDoctorPayload): Promise<SATResult<void, SATError>> {
    return this.createMedicalExpertByUrl(URLList.DOCTOR.CREATE_NURSE, payload);
  }

  async createCro(payload: CreateDoctorPayload): Promise<SATResult<void, SATError>> {
    return this.createMedicalExpertByUrl(URLList.DOCTOR.CREATE_CRO, payload);
  }

  async updateDoctor(uuid: string, payload: Record<string, unknown>): Promise<SATResult<void, SATError>> {
    const url = `${URLList.DOCTOR.UPDATE}/${uuid}`;
    try {
      await withApiLogging(this.logger, { method: "PUT", url, payload }, async () => {
        const response = await this.client.put(url, payload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async deactivateDoctor(uuid: string): Promise<SATResult<void, SATError>> {
    const url = `${URLList.DOCTOR.DEACTIVATE}/${uuid}/deactivate`;
    try {
      await withApiLogging(this.logger, { method: "DELETE", url }, async () => {
        const response = await this.client.delete(url);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async activateDoctor(uuid: string): Promise<SATResult<void, SATError>> {
    const url = `${URLList.DOCTOR.ACTIVATE}/${uuid}/activate`;
    try {
      await withApiLogging(this.logger, { method: "PUT", url }, async () => {
        const response = await this.client.put(url);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async resendAccountSetupEmail(uuid: string): Promise<SATResult<void, SATError>> {
    const url = `${URLList.DOCTOR.RESEND_ACCOUNT_SETUP_EMAIL}/${uuid}/account-setup-email/resend`;
    try {
      await withApiLogging(this.logger, { method: "POST", url }, async () => {
        const response = await this.client.post(url);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async updatePasswordExpiryDate(
    uuid: string,
    snUpdatedAt: string,
  ): Promise<SATResult<void, SATError>> {
    const url = URLList.AUTH.UPDATE_PASSWORD_EXPIRE_DATE;
    const payload = { uuid, snUpdatedAt };
    try {
      await withApiLogging(this.logger, { method: "PUT", url, payload }, async () => {
        const response = await this.client.put(url, payload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  private async createMedicalExpertByUrl(
    url: string,
    payload: CreateDoctorPayload,
  ): Promise<SATResult<void, SATError>> {
    const apiPayload = doctorCreateRequestPayload(payload);
    try {
      await withApiLogging(this.logger, { method: "POST", url, payload: apiPayload }, async () => {
        const response = await this.client.post(url, apiPayload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }
}

function doctorCreateRequestPayload(payload: CreateDoctorPayload): Omit<CreateDoctorPayload, "countryCode"> {
  const { countryCode: _countryCode, ...apiPayload } = payload;
  return apiPayload;
}

const DOCTOR_LIST_RESPONSE_KEYS = [
  "doctorList",
  "medicalExpertList",
  "chargeList",
  "doctors",
  "dataList",
  "items",
  "list",
  "result",
  "content",
  "data",
] as const;
